import { openDB } from 'idb'

export default class Database
{
    
    constructor( core )
    {
        
        if ( !Database.instance ) {
            
            this.logger = core.getLogger()
            this.uuid = core.getUuid()
            this.eventManager = core.getEventManager()
            
            this.setState = ( key, value ) =>
            {
                return core.setState( key, value )
            }
            
            this.getState = ( key ) =>
            {
                return core.getState( key )
            }
            
            this.databaseName = 'lovepod'
            this.databaseVersion = 3
            this.resetState = false
            
            this.logger.cconstructed( 'Database:constructor', 'initializing database "' + this.databaseName + '", version ' + this.databaseVersion )
            
            this.db = this.openDatabase()
            Database.instance = this
            
        }
        
        return Database.instance
        
    }
    
    setResetState( state )
    {
        this.resetState = state
    }
    
    getResetState()
    {
        return this.resetState
    }
    
    /**
     * openDatabase
     * - opens indexeddb connection and returns database promise
     * @param dropflag (boolean, drop database or not)
     * @returns {Promise<IDBPDatabase<unknown>>}
     */
    openDatabase( dropflag )
    {
        
        let fresh = false
        let dbPromise = openDB( this.databaseName, this.databaseVersion, {
            upgrade( db, oldVersion, newVersion, upgradeDB )
            {
                
                fresh = true
                
                switch ( oldVersion ) {
                    case 0:
                        upgradeDB.db.createObjectStore( 'session' )
                        upgradeDB.db.createObjectStore( 'settings' )
                        upgradeDB.db.createObjectStore( 'profile' )
                        upgradeDB.db.createObjectStore( 'contacts' )
                    // falls through
                    case 1:
                        upgradeDB.db.createObjectStore( 'storage' )
                    // falls through
                    case 2:
                        upgradeDB.db.createObjectStore( 'statistics' )
                    // falls through
                }
                
            }
        } ).then( ( db ) =>
        {
            
            this.setState( 'mxCache-initialize-after-sync', fresh )
            
            if ( undefined !== dropflag ) {
                
                this.logger.log( 'database dropflag is set: will destroy objectstores' )
                
                db.close()
                
                setTimeout( () =>
                {
                    
                    let req = indexedDB.deleteDatabase( this.databaseName )
                    
                    req.onsuccess = () =>
                    {
                        this.logger.log( 'database dropped' )
                    }
                    
                }, 10 )
                
            }
            return db
            
        } )
        
        return dbPromise
        
    }
    
    truncateTables( tables )
    {
        
        return new Promise(
            resolve =>
            {
                
                if ( 0 < tables.length ) {
                    
                    let table = tables[ 0 ]
                    this.db.then( database =>
                    {
                        
                        const transaction = database.transaction( table, 'readwrite' )
                        transaction.objectStore( table ).clear()
                        tables.shift()
                        return resolve( this.truncateTables( tables ) )
                        
                    } )
                    
                } else {
                    return resolve( true )
                }
                
            } )
        
    }
    
    truncateStorage()
    {
        return new Promise( resolve =>
        {
            
            let todo = [
                'session',
                'settings',
                'profile',
                'contacts',
                'storage'
            ]
            
            this.truncateTables( todo )
                .then( () =>
                {
                    return resolve()
                } )
            
        } )
    }
    
    truncateAll( stayopen )
    {
        
        return new Promise(
            resolve =>
            {
                
                this.truncateStorage()
                    .then( () =>
                    {
                        
                        if ( true === stayopen ) {
                            return resolve()
                        } else {
                            return resolve( this.closeDatabase() )
                        }
                        
                    } )
                
            } )
        
    }
    
    /**
     * closeDatabase
     * - closes indexeddb connection
     * @returns {Promise<unknown>}
     */
    closeDatabase()
    {
        
        return new Promise( resolve =>
        {
            
            this.db.then( db =>
                {
                    db.close()
                    this.logger.clog( 'Database:closeDatabase', 'closed database connection.' )
                    return resolve()
                } )
                .catch( error =>
                {
                    this.logger.cerror( 'Database:closeDatabase', 'error closing database connection: ' + error )
                } )
            
        } )
        
    }
    
    /**
     * delete
     * @param storage
     * @param key
     * @returns {Promise<DB | never>}
     */
    delete( storage, key )
    {
        return new Promise( ( resolve, reject ) =>
        {
            
            this.db.then( database =>
                {
                    
                    const transaction = database.transaction( storage, 'readwrite' )
                    transaction.objectStore( storage ).delete( key )
                    return resolve()
                    
                } )
                .catch( () =>
                {
                    return reject()
                } )
            
        } )
    }
    
    write( storeName, key, value )
    {
        
        return new Promise( ( resolve, reject ) =>
        {
            
            this.db.then( database =>
                {
                    
                    const transaction = database.transaction( storeName, 'readwrite' )
                    
                    if ( key === undefined ) {
                        return reject( 'ERROR_KEY_MISSING' )
                    } else {
                        
                        let clone = JSON.parse( JSON.stringify( value ) )
                        transaction.objectStore( storeName ).put( clone, key )
                                   .finally( success =>
                                   {
                                       return resolve( success )
                                   } )
                                   .catch( () =>
                                   {
                                       return reject( 'ERROR WRITE DB' )
                                   } )
                    }
                    
                } )
                .catch( error =>
                {
                    return reject( 'ERROR_DB_OPEN:' + error )
                } )
            
        } )
        
    }
    
    read( storeName, key )
    {
        
        return new Promise( ( resolve, reject ) =>
        {
            
            this.db.then( database =>
                {
                    
                    const transaction = database.transaction( storeName, 'readonly' )
                                                .objectStore( storeName )
                    
                    transaction.get( key )
                               .then( ( obj ) =>
                               {
                                   
                                   if ( undefined !== obj ) {
                                       return resolve( obj )
                                   }
                                   
                                   return reject( 'ERROR_NOT_FOUND' )
                                   
                               } )
                               .catch( e =>
                               {
                                   return reject( e )
                               } )
                    
                } )
                .catch( () =>
                {
                    return reject( 'ERROR_DB_READ' )
                } )
            
        } )
        
    }
    
    /**
     * writeStorage
     * @param key
     * @param value
     * @returns {Promise<any>}
     */
    writeStorage( key, value )
    {
        
        return new Promise(
            ( resolve, reject ) =>
            {
                
                this.write( 'storage', key, value )
                    .then( () =>
                    {
                        return resolve()
                    } )
                    .catch( () =>
                    {
                        
                        if ( true !== this.getState( 'app-resetting' ) ) {
                            
                            this.eventManager
                                .dispatch( 'ui-blocker-showBlocker',
                                    {
                                        mode:   'error',
                                        title:  'Oh nein!',
                                        text:   '<strong>entzettelt</strong> funktioniert auf deinem Browser leider nicht! Damit unsere App funktionieren kann, benötigt sie Zugriff auf die lokale Datenbank deines Browsers, damit deine Daten auch offline verfügbar sind.<br/><br/><strong>Hinweis: Wenn du den "Private Mode" deines Browsers verwendest, kann das bereits der Grund für diesen Fehler sein.</strong>',
                                        locked: true
                                    } )
                            
                        }
                        
                        return reject( 'ERROR WRITE STORAGE' )
                        
                    } )
                
            } )
    }
    
    /**
     * readStorage
     * @param key
     * @returns {Promise<any>}
     */
    readStorage( key )
    {
        
        return new Promise(
            ( resolve, reject ) =>
            {
                
                this.read( 'storage', key )
                    .then( result =>
                    {
                        return resolve( result )
                    } )
                    .catch( () =>
                    {
                        return reject( 'ERROR WRITE STORAGE' )
                    } )
                
            } )
    }
    
    
    /**
     * readAllFromStorage
     * @param storage
     * @returns {Promise<unknown>}
     */
    readAllFromStorage( storage )
    {
        
        return new Promise( ( resolve, reject ) =>
        {
            
            this.db.then( database =>
            {
                
                const transactKeys = database.transaction( storage, 'readonly' ),
                      storeKeys    = transactKeys.objectStore( storage )
                
                let items = []
                
                storeKeys.getAllKeys()
                         .then( result =>
                         {
                             
                             for ( let r in result ) {
                                 items.push( {
                                     key:  result[ r ],
                                     item: null
                                 } )
                             }
                             
                             const transaction = database.transaction( storage, 'readonly' )
                             for ( let l in items ) {
                                 
                                 transaction.objectStore( storage )
                                            .get( items[ l ].key )
                                            .then( object =>
                                            {
                                                items[ l ].item = object
                                            } )
                                            .catch( () =>
                                            {
                                            } )
                                 
                             }
                             transaction.done.then( () =>
                             {
                                 
                                 return resolve( items )
                                 
                             } )
                             
                             
                         } )
                         .catch( error =>
                         {
                             this.logger.cerror( 'Database::readAllObjects', 'objectStore.getAllKeys failed:', error )
                             return reject( error )
                         } )
                
            } )
            
        } )
        
    }
    
}